import React, { useEffect, useState } from 'react'
import Dropzone from 'react-dropzone'
import classNames from 'classnames'
import {
  Button,
  Grid,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
} from '@material-ui/core'
import { get, isEqual } from 'lodash'
import { styles } from './styles'
import { emailPlaceholderRegex } from 'Utils/Regexp'
import usePrevious from 'Utils/Hooks/usePrevious'
import CloseIcon from 'Assets/Images/icons/close'
import { getBase64 } from 'Containers/StatsDeliverability/UploadFile/utils'
import SubmitButton from 'Components/Form/Button'
import Form from 'Components/Form'
import RichTextEditor from 'Components/RichTextEditor'
import EmojiPicker from 'Components/EmojiPicker'
import { optionsPlaceholder } from '../../../../../../../options'
import GroupSelect from '../../../../../../GroupSelect'
import { useEditSequenceContext } from '../../../../context/EditSequenceContext'
import PlaceholderEditor from '../PlaceholderEditor'

const initialValues = {
  name: { value: '', error: false },
  sender: { value: '', error: false },
  cc: { value: '', error: false },
  bcc: { value: '', error: false },
  subject: { value: '', error: false },
  body: {
    value: [
      {
        type: 'paragraph',
        align: 'left',
        children: [{ text: '' }],
      },
    ],
    error: false,
  },
}

const emailRegex = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/

const normalizeDynamicValues = (value, key) => {
  return typeof value[key] === 'string' ? JSON.parse(value[key]) : value[key]
}

const formatDataToValuesAndErrors = email => {
  const keys = Object.keys(email)
  return keys.reduce((acc, key) => {
    if (key === 'cc' || key === 'bcc') {
      acc[key] = {
        value: email[key] ? email[key].join(', ') : '',
        error: false,
      }
    } else if (key === 'customDynamicValues') {
      const value = normalizeDynamicValues(email, key)
      acc[key] = { value, error: false }
    } else if (['name', 'subject', 'sender', 'body'].includes(key)) {
      acc[key] = { value: email[key], error: false }
    }
    return acc
  }, {})
}

const parsePlaceholders = (placeholders, dynamicData) => {
  return placeholders.reduce((obj, placeholder) => {
    return { ...obj, [placeholder]: dynamicData[placeholder] }
  }, {})
}

export const CreateEditEmailTemplateModal = ({
  classes,
  open,
  controlModal,
  cityId,
  cityEmail,
  userEmail,
  onSubmit,
  emailId,
}) => {
  const { editSequenceContextState } = useEditSequenceContext()
  const { sequence, defaultDynamicValues } = editSequenceContextState
  const [files, setFiles] = useState([])
  const [deletedFilesIds, setDeletedFilesIds] = useState([])
  const [insertedValue, setInsertedValue] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [emojiPicked, setEmojiPicked] = useState(null)
  const [inputsData, setInputsData] = useState(initialValues)
  const [lastClicked, setLastClicked] = useState({
    component: null,
    position: 0,
  })
  const emailOptions = [cityEmail, userEmail]

  const bodyStr = JSON.stringify(inputsData.body)
  const placeholders = bodyStr.match(emailPlaceholderRegex)
  const customDynamicValues = get(inputsData, 'customDynamicValues.value', {})
  const [placeholdersState, setPlaceholdersState] = useState(
    parsePlaceholders(placeholders || [], customDynamicValues),
  )
  const prevPlaceholders = usePrevious(placeholders)
  const prevCustomDynamicValues = usePrevious(customDynamicValues)
  const placeholderChanged = !isEqual(placeholders, prevPlaceholders)
  const customDynamicValuesChanged = !isEqual(
    customDynamicValues,
    prevCustomDynamicValues,
  )

  useEffect(() => {
    if (placeholders && placeholderChanged) {
      setPlaceholdersState(state => parsePlaceholders(placeholders, state))
    }
  }, [placeholders, placeholderChanged])

  useEffect(() => {
    if (placeholders && customDynamicValues && customDynamicValuesChanged) {
      setPlaceholdersState(parsePlaceholders(placeholders, customDynamicValues))
    }
  }, [placeholders, customDynamicValues])

  useEffect(() => {
    setDisabled(
      Object.values(inputsData)
        .map(item => item.error)
        .some(item => item),
    )
  }, [inputsData])

  useEffect(() => {
    if (!emailId) {
      setInputsData(initialValues)
      setFiles([])
      setDeletedFilesIds([])
    } else {
      const action = sequence.Action.find(
        action => action.template.id === emailId,
      )
      const newInputValues = formatDataToValuesAndErrors(action.template)
      setInputsData(newInputValues)
      if (action && action.template.files) {
        setFiles(action.template.files)
      }
      if (action && action.template.deletedFilesIds) {
        setDeletedFilesIds(action.template.deletedFilesIds)
      }
    }
    open ? setIsOpen(true) : setIsOpen(false)
  }, [open, emailId])

  const handleChangeInputPlaceholder = ({ target }) => {
    setPlaceholdersState(prev => ({
      ...prev,
      [target.name]: target.value,
    }))
  }

  const handleClearInputPlaceholder = input => {
    setPlaceholdersState(prev => ({
      ...prev,
      [input]: '',
    }))
  }

  const handleSaveFile = async files => {
    const newFiles = []
    let ignoredSmallFiles = 0
    let ignoredBigFiles = 0
    for (const file of files) {
      if (file.size === 0) {
        ignoredSmallFiles++
        continue
      }
      if (file.size > 25000000) {
        ignoredBigFiles++
        continue
      }
      const fileName = file.name
      const fileBase64 = await getBase64(file)
      newFiles.push({
        fileName,
        file: fileBase64,
        id: `temp-file-${new Date().getTime()}`,
      })
    }
    setFiles(prev => [...prev, ...newFiles])
    let message = ''
    if (ignoredSmallFiles) {
      message += `${ignoredSmallFiles} file(s) ignored because they were empty.`
    }
    if (ignoredBigFiles) {
      if (message) {
        message += '\n'
      }
      message += `${ignoredBigFiles} file(s) ignored because they were over 25MB.`
    }
    if (message) {
      alert(message)
    }
  }

  const handleDeleteFile = file => {
    if (file.id) {
      setFiles(prev => prev.filter(item => file.id !== item.id))
      if (!file.id.includes('temp')) {
        setDeletedFilesIds(prev => [...prev, file.id])
      }
    } else {
      setFiles(prev => prev.filter(item => item.fileName !== file.fileName))
    }
  }

  const handleSubjectInsert = value => {
    const input = document.getElementById('subjectInput')

    let pos = lastClicked.position
    const front = input.value.substring(0, pos)
    const back = input.value.substring(pos, input.value.length)
    const newValue = front + value + back
    const event = {
      target: {
        value: newValue,
      },
    }
    handleChange({ type: 'subject', event })
    setLastClicked(prev => ({ ...prev, position: pos + value.length }))
  }

  const getClickedPosition = component => {
    if (component === 'subject') {
      return document.getElementById('subjectInput').selectionStart
    }
  }

  const handleInsertPlaceholder = value => {
    if (lastClicked.component === 'body') {
      setInsertedValue(value)
    } else if (lastClicked.component === 'subject') {
      handleSubjectInsert(value)
    }
  }

  const handleEmojiClick = (event, emojiObject) => {
    setEmojiPicked(emojiObject.emoji)
  }

  useEffect(() => {
    if (!emojiPicked) return
    if (lastClicked.component === 'body') {
      setInsertedValue(emojiPicked)
    } else if (lastClicked.component === 'subject') {
      handleSubjectInsert(emojiPicked)
    }
    setEmojiPicked(null)
  }, [emojiPicked])

  const handleSubmit = () => {
    inputsData.customDynamicValues = {
      value: placeholdersState,
      error: false,
    }

    const newInputValues = Object.keys(inputsData).reduce((acc, key) => {
      acc[key] = {
        value: inputsData[key].value,
        error: ['cc', 'bcc', 'customDynamicValues'].includes(key)
          ? false
          : !inputsData[key].value.length,
      }
      return acc
    }, {})
    const disabled = Object.values(newInputValues)
      .map(item => item.error)
      .some(item => item)

    if (disabled) {
      setInputsData(newInputValues)
    } else {
      setInsertedValue(null)
      onSubmit({
        inputsData,
        files: [...files],
        deletedFilesIds,
      })
    }
  }

  const handleChange = ({ type, event }) => {
    const isCCBCC = type === 'cc' || type === 'bcc'
    if (isCCBCC) {
      const newValue = event.target.value
      let error = false
      if (newValue.length > 0) {
        error = newValue
          .split(',')
          .map(email => email.trim())
          .some(email => !emailRegex.test(email))
      }
      const newInputValues = {
        ...inputsData,
        [type]: { value: newValue, error },
      }
      setInputsData(newInputValues)
    } else {
      setInputsData({
        ...inputsData,
        [type]: { value: event.target.value, error: !event.target.value },
      })
    }
  }

  const handleBodyChange = value => {
    setInputsData({
      ...inputsData,
      body: { value, error: !value },
    })
  }

  const handleCancel = () => {
    setInsertedValue(null)
    controlModal({ open: false, id: null })
  }

  const errorComponent = error => {
    return error ? <div style={styles.error}>field is required</div> : null
  }

  return (
    <Form>
      <Modal open={open} onClose={handleCancel} disablePortal={true}>
        <div className={classes.body}>
          <span className={classNames(classes.boldText)}>
            {emailId ? 'Edit' : 'Create new'} email
          </span>
          <div>
            <div
              className={classNames(
                classes.rowContainer,
                classes.wrapperSelect,
              )}
            >
              <span className={`${classes.smallText} ${classes.nameLabel}`}>
                Name
              </span>
              <OutlinedInput
                placeholder="Select a name"
                value={inputsData.name.value}
                onChange={event => handleChange({ type: 'name', event })}
                style={styles.input}
                error={inputsData.name.error}
              />
            </div>
            {errorComponent(inputsData.name.error)}
            <div
              className={classNames(classes.rowContainer, classes.spaceBetween)}
            >
              <div
                className={`${classes.wrapperSelect} ${
                  classes.senderSelectContainer
                }`}
              >
                <span className={classes.smallText}>Send email from:</span>
                <Select
                  value={inputsData.sender.value}
                  onChange={event => handleChange({ type: 'sender', event })}
                  className={classes.senderSelector}
                  error={inputsData.sender.error}
                  variant="outlined"
                >
                  {emailOptions.map(email => (
                    <MenuItem value={email}>
                      <div className={classes.menuItem}>{email}</div>
                    </MenuItem>
                  ))}
                </Select>
                {errorComponent(inputsData.sender.error)}
              </div>
              <div>
                <GroupSelect
                  label="Insert Placeholder"
                  options={optionsPlaceholder}
                  onChange={handleInsertPlaceholder}
                  value={insertedValue}
                  disableDefaultValue={true}
                  displayColumn
                />
              </div>
            </div>
          </div>
          <Grid
            container
            xs={12}
            direction="column"
            className={classes.inputContainer}
          >
            <Grid className={classes.rowContainer}>
              <span className={classes.ccText}>cc:</span>
              <OutlinedInput
                placeholder="Separate recipients with a “,” and leave empty if N/A"
                value={inputsData.cc.value}
                onChange={event => handleChange({ type: 'cc', event })}
                style={styles.input}
              />
            </Grid>
            <Grid className={classes.rowContainer}>
              <span className={classes.bccText}>bcc:</span>
              <OutlinedInput
                placeholder="Separate recipients with a “,” and leave empty if N/A"
                value={inputsData.bcc.value}
                onChange={event => handleChange({ type: 'bcc', event })}
                style={styles.input}
              />
            </Grid>
            <Grid className={classes.rowContainer}>
              <span className={classes.sbjtText}>Sbjt:</span>
              <OutlinedInput
                id={'subjectInput'}
                placeholder="Bill [Invoice Number] from [Trading Name] is due"
                value={inputsData.subject.value}
                onChange={event => handleChange({ type: 'subject', event })}
                style={styles.input}
                error={inputsData.subject.error}
                onClick={() =>
                  setLastClicked({
                    component: 'subject',
                    position: getClickedPosition('subject'),
                  })
                }
              />
            </Grid>
            {errorComponent(inputsData.subject.error)}
          </Grid>
          {isOpen ? (
            <Grid container xs={12} className={classes.rowContainer}>
              <RichTextEditor
                key={`email-${emailId}`}
                index={`email-${emailId}`}
                markTools
                listTools
                extraTools={<EmojiPicker onEmojiClick={handleEmojiClick} />}
                fontSize="12px"
                rows="20"
                textToInsert={insertedValue}
                onChange={handleBodyChange}
                value={inputsData.body.value}
                onClick={() =>
                  setLastClicked({ component: 'body', position: null })
                }
              />
            </Grid>
          ) : null}
          {errorComponent(inputsData.body.error)}
          <PlaceholderEditor
            placeholders={placeholders}
            defaultDynamicValues={defaultDynamicValues}
            customDynamicValues={placeholdersState}
            onClearInput={handleClearInputPlaceholder}
            onChangeInput={handleChangeInputPlaceholder}
          />
          <Grid
            container
            direction="row"
            alignItems="center"
            style={{ paddingTop: '10px' }}
            xs={12}
          >
            <Grid container xs={8} direction="row">
              <Grid item>
                <Dropzone
                  accept={''}
                  activeClassName="dropzoneActive"
                  className="dropzone"
                  multiple={true}
                  onDrop={handleSaveFile}
                  disabled={false}
                >
                  <div className={classes.dropzone}>Drag and drop</div>
                </Dropzone>
              </Grid>
              {[...files].length ? (
                <Grid item direction="column">
                  <div className={classes.filesContainer}>
                    {[...files].map(file => (
                      <div>
                        {file.id ? (
                          <a
                            className={classes.filesItem}
                            target="_blank"
                            href={file.fileUrl}
                          >
                            {file.fileName}
                          </a>
                        ) : (
                          <span className={classes.filesItem}>
                            {file.fileName}
                          </span>
                        )}{' '}
                        <CloseIcon
                          onClick={() => handleDeleteFile(file)}
                          style={{ height: 10, marginLeft: 5 }}
                        />
                      </div>
                    ))}
                  </div>
                </Grid>
              ) : null}
            </Grid>
            <Grid container xs justifyContent="flex-end">
              <Button
                variant="text"
                onClick={handleCancel}
                style={{ marginRight: '16px' }}
                className={classes.button}
              >
                Cancel
              </Button>
              <SubmitButton
                className={classes.submitButton}
                onClick={handleSubmit}
                disabled={disabled}
              >
                Save
              </SubmitButton>
            </Grid>
          </Grid>
        </div>
      </Modal>
    </Form>
  )
}
